/**
 * @file Vue-related helpers
 */
import { initializeAuthenticationChannel } from '@@/bits/auth_broadcast_channel'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { hasPerfUrlParam } from '@@/bits/dev_url_params'
import { DOMContentLoadedPromise } from '@@/bits/dom'
import environment from '@@/bits/environment'
import * as errorTracker from '@@/bits/error_tracker'
import fixMobileViewport from '@@/bits/fix_mobile_viewport'
import { isAppUsing, isDebugMode } from '@@/bits/flip'
import window from '@@/bits/global'
import { np__, N__, n__, p__, __ } from '@@/bits/intl'
import { reload } from '@@/bits/location'
import { injectDesignClassesIfEnabled } from '@@/bits/vue_inject_design_classes'
import '@@/styles/tailwind.css'
import type { Id } from '@@/types'
import { usePostHog } from '@@/vuecomposables/posthog'
import { configure as configureArvo } from '@padlet/arvo'
import { isEqual } from 'es-toolkit'
import { isObject } from 'es-toolkit/compat'
import { createPinia, type Pinia as PiniaStore } from 'pinia'
import type { PostHog } from 'posthog-js'
import {
  createApp,
  defineAsyncComponent,
  watch,
  watchEffect,
  type App,
  type Component,
  type WatchCallback,
  type WatchEffect,
  type WatchOptions,
  type WatchOptionsBase,
  type WatchSource,
  type WatchStopHandle,
} from 'vue'
import { createStore, type Store as VuexStore, type StoreOptions as VuexStoreOptions } from 'vuex'

// Indicates errors that can be potentially fixed by reloading the page.
export class PageReloadableError extends Error {}

export interface VueApp extends App {
  $store: VuexStore<any>
  $pinia: PiniaStore
  $posthog?: PostHog
}

interface VueAppOptions {
  // Element to mount on
  el?: string

  // Component config
  rootComponent: Component
  rootComponentProps?: () => Record<string, any>
  loadPlugins?: (app: App) => Promise<void>
  created?: () => void // initialize store
  mounted?: () => void
  beforeMountPromises?: Array<Promise<any>>
  otherVueOptions?: Record<string, any>

  // Store
  store?: VuexStore<any>
  storeOptions?: VuexStoreOptions<any>
  initializeStore?: (store: VuexStore<any>) => void
  usePinia?: boolean
  realtime?: boolean // indicates that the app should connect to realtime as part of setting up vuex store
  /**
   * Use to initialize Pinia store. Use this to setup pinia store with starting data (e.g. from `window`)
   * @param store
   * @returns
   */
  initializePiniaStore?: (store: PiniaStore) => void

  // Error-tracking
  environment?: string
  locale?: string
  release?: string

  // Others
  disableAccessibilityChecks?: boolean
  useVueQuery?: boolean

  // Authentication broadcast channel
  // If they are not provided, the default behavior is to reload the page.
  onGlobalLogin?: () => void | Promise<void>
  onGlobalLogout?: () => void | Promise<void>
}

// Temporary no-op function. Will be replaced when we move to Vue3.
// // Context: https://v3-migration.vuejs.org/breaking-changes/async-components.html
// export const defineAsyncComponent = (component: any): any => component

export async function setupVueApp(options: VueAppOptions): Promise<VueApp | undefined> {
  let vueApp: VueApp | undefined

  try {
    await Promise.all([DOMContentLoadedPromise, ...(options.beforeMountPromises ?? [])])

    // Configure Arvo if available
    if (window?.ww?.vueStartingState?.arvoConfig != null) {
      void configureArvo(window.ww.vueStartingState.arvoConfig)
    }

    // Mobile browsers handle 100% height in ways we don't like. Fix that.
    fixMobileViewport()

    // Evaluate props only after DOMContentLoaded to ensure scripts that set globals on `window` have run.
    const props = typeof options.rootComponentProps === 'function' ? options.rootComponentProps() : {}

    // Create Vue 3 app
    vueApp = createApp(options.rootComponent, props) as VueApp
    // We re-assign window.app since in vue3, window.app is initialized to be the dom node instead of the app instance
    window.app = vueApp

    // Add design classes to component root elements if ?injectDesignClasses query param is present
    injectDesignClassesIfEnabled(vueApp)

    // Setup stores
    const { vuexStore, piniaStore } = setupStores(vueApp, options)

    // Register Vuex store if available
    if (vuexStore !== undefined && vuexStore !== null) {
      // Add store to global properties for backward compatibility
      vueApp.config.globalProperties.$store = vuexStore
      window.app.$store = vuexStore
    }

    // Register Pinia store if available
    if (piniaStore !== undefined && piniaStore !== null) {
      vueApp.use(piniaStore)
    }

    // Set up lifecycle hooks
    if (options.created !== undefined && options.created !== null) {
      vueApp.config.globalProperties.$created = options.created
    }

    if (options.mounted !== undefined && options.mounted !== null) {
      vueApp.config.globalProperties.$mounted = options.mounted
    }

    // Apply other Vue options
    if (
      options.otherVueOptions !== undefined &&
      options.otherVueOptions !== null &&
      Object.keys(options.otherVueOptions).length > 0
    ) {
      Object.entries(options.otherVueOptions).forEach(([key, value]) => {
        if (vueApp != null) {
          vueApp.config.globalProperties[`$${key}`] = value
        }
      })
    }

    // Setup error tracking
    setupErrorTracking(vueApp, options)

    // Setup internationalization
    setupIntl(vueApp)

    // Setup plugins
    await setupPlugins(vueApp, options)

    // Setup Vue Query if needed
    if (options.useVueQuery === true && vueApp) {
      const queryModules = await Promise.all([import('@tanstack/vue-query'), import('@@/bits/query_client')])
      const vueQueryModule = queryModules[0]
      const queryClientModule = queryModules[1]
      const queryClient = await queryClientModule.createQueryClient()

      vueApp.use(vueQueryModule.VueQueryPlugin, { queryClient })
      window.queryClient = queryClient
    }

    // Configure Vue app
    if (vueApp && process.env.NODE_ENV === 'production' && isDebugMode) {
      vueApp.config.performance = true
    } else if (vueApp) {
      vueApp.config.performance = hasPerfUrlParam()
    }

    // Set ignored elements
    // if (vueApp) {
    //   vueApp.config.compilerOptions = {
    //     isCustomElement: (tag) =>
    //       ['trix-editor', 'lottie-player', 'dotlottie-wc', MENTION_ELEMENT_TAG_NAME].includes(tag),
    //   }
    // }

    // Initialize Pinia store
    if (
      vueApp &&
      options.usePinia === true &&
      typeof options.initializePiniaStore === 'function' &&
      piniaStore != null
    ) {
      options.initializePiniaStore(piniaStore)
    }
    if (vueApp && piniaStore != null && options.usePinia === true) {
      window.app.$pinia = piniaStore
    }

    if (options.realtime === true) {
      vuexStore?.dispatch('realtime/connect')
    }

    // Initialize authentication channel
    initializeAuthenticationChannel({
      onLogin: options.onGlobalLogin,
      onLogout: options.onGlobalLogout,
    })

    // window.app ??= vueApp
    // Mount the app if an element is specified
    if (typeof options.el === 'string' && options.el.length > 0) {
      // Avoid extra `div` wrapper after mounting the app.
      // @see https://v3-migration.vuejs.org/breaking-changes/mount-changes.html
      const fragment = document.createDocumentFragment()
      vueApp.mount(fragment)

      // Initialize PostHog
      void setupPostHog(vueApp)

      const target = document.querySelector(options.el)
      if (target == null || target.parentNode == null) {
        throw new Error('Unable to find element to mount Vue app.')
      }
      target?.parentNode?.replaceChild(fragment, target)
    }
  } catch (e) {
    if (e instanceof PageReloadableError) {
      reload()
    } else {
      errorTracker.captureException(e)
    }
    return undefined
  }

  return vueApp
}

function setupErrorTracking(app: App, options: VueAppOptions): void {
  errorTracker.init({
    vue: app,
    environment: options.environment ?? environment,
    locale: options.locale ?? getHyphenatedCurrentLocale(),
    release: options.release ?? document?.documentElement?.dataset?.version,
  })
}

export function setupIntl(app: App): void {
  app.config.globalProperties.__ = __
  app.config.globalProperties.N__ = N__
  app.config.globalProperties.p__ = p__
  app.config.globalProperties.n__ = n__
  app.config.globalProperties.np__ = np__
}

async function setupPlugins(app: App, options: VueAppOptions): Promise<void> {
  // Use accessibility tools (in development) by default
  const disableAccessibilityChecks = true
  if (!disableAccessibilityChecks) void useVueAxePlugin(app)

  // Use other page-specific plugins
  if (options.loadPlugins != null && typeof options.loadPlugins === 'function') {
    await options.loadPlugins(app)
  }
}

/**
 * perf=true is a url param that disables logging and axe in development
 * this is useful for debugging performance issues
 * @see @@/bits/dev_url_params
 */
async function useVueAxePlugin(app: App): Promise<void> {
  if (environment === 'production') return
  if (hasPerfUrlParam()) return
  const VueAxePlugin = (await import('vue-axe')).default
  app.use(VueAxePlugin, {
    allowConsoleClears: false,
    clearConsoleOnUpdate: false,
  })
}

function setupVuexStore(app: App, options: VueAppOptions): VuexStore<any> | undefined {
  // Allow already-initialized store for now, to make porting Surface easier.
  let store = options.store

  if (isObject(options.storeOptions)) {
    store = createStore(options.storeOptions)
  }

  if (store !== undefined && typeof options.initializeStore === 'function') {
    options.initializeStore(store)
    app.use(store)
  }

  return store
}

function setupPiniaStore(): PiniaStore | undefined {
  const store = createPinia()

  return store
}

function setupStores(
  app: App,
  options: VueAppOptions,
): {
  vuexStore: VuexStore<any> | undefined
  piniaStore: PiniaStore | undefined
} {
  const usePinia = options.usePinia ?? false
  const vuexStore = setupVuexStore(app, options)
  const piniaStore = usePinia ? setupPiniaStore() : undefined

  return { vuexStore, piniaStore }
}

async function setupPostHog(app: VueApp): Promise<void> {
  if (isAppUsing('posthog') && app.$posthog == null) {
    const { posthog } = await usePostHog()
    app.$posthog = posthog
  }
}

type IdArrayMap = Record<Id, Id[]>
function stableSync(target: IdArrayMap, source: IdArrayMap): IdArrayMap {
  new Set([...Object.keys(source), ...Object.keys(target)]).forEach(function (key) {
    const sourceIds = source[key]
    const targetIds = target[key]

    if (sourceIds == null && targetIds == null) {
      return
    }

    if (sourceIds != null && targetIds != null && isEqual(sourceIds, targetIds)) {
      return
    }

    if (sourceIds != null) {
      target[key] = [...sourceIds]
    } else {
      // Use Reflect.deleteProperty instead of delete operator
      Reflect.deleteProperty(target, key)
    }
  })
  return target
}

/**********************
 * Unbounded watchers *
 **********************

 These watcher functions register the watcher asynchronously so that
 they are not bound to any particular component instance. This is
 useful in context like a Pinia store where the store may be used
 in multiple components but you don't want the watchers in the store
 to be stopped automatically when a component is unmounted.

 Read more about it here: https://vuejs.org/guide/essentials/watchers.html#stopping-a-watcher

 To preserve the type safety of these custom watchers, I copied the
 function signatures from node_modules/vue/types/v3-generated.d.ts
 and only changed the return type to be Promise<WatchStopHandle>.
 **********************/

type MultiWatchSources = Array<WatchSource<unknown> | object>

type MapSources<T, Immediate> = {
  [K in keyof T]: T[K] extends WatchSource<infer V>
    ? Immediate extends true
      ? V | undefined
      : V
    : T[K] extends object
      ? Immediate extends true
        ? T[K] | undefined
        : T[K]
      : never
}

async function unboundedWatch<T extends MultiWatchSources, Immediate extends Readonly<boolean> = false>(
  sources: [...T],
  cb: WatchCallback<MapSources<T, false>, MapSources<T, Immediate>>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch<T extends Readonly<MultiWatchSources>, Immediate extends Readonly<boolean> = false>(
  source: T,
  cb: WatchCallback<MapSources<T, false>, MapSources<T, Immediate>>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch<T, Immediate extends Readonly<boolean> = false>(
  source: WatchSource<T>,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch<T extends object, Immediate extends Readonly<boolean> = false>(
  source: T,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: WatchOptions<Immediate>,
): Promise<WatchStopHandle>

async function unboundedWatch(
  sourceOrSources: any,
  cb: WatchCallback,
  options?: WatchOptions,
): Promise<WatchStopHandle> {
  return await new Promise((resolve) => {
    setTimeout(() => {
      const stopHandle = watch(sourceOrSources, cb, options)
      resolve(stopHandle)
    }, 0)
  })
}

async function unboundedWatchEffect(effect: WatchEffect, options?: WatchOptionsBase): Promise<WatchStopHandle> {
  return await new Promise((resolve) => {
    setTimeout(() => {
      const stopHandle = watchEffect(effect, options)
      resolve(stopHandle)
    }, 0)
  })
}

/**
 * Direct property setter for Vue 3 reactivity
 *
 * In Vue 3, we can directly set properties on reactive objects due to the Proxy-based reactivity system.
 * This function maintains the same signature as Vue 2's Vue.set for backward compatibility.
 *
 * @param target The object or array to set the property on
 * @param key The property name or array index
 * @param value The value to set
 * @returns The set value
 */
function vSet<T, K extends keyof T>(target: T, key: K, value: T[K]): T[K]
function vSet<T>(target: T[], key: number, value: T): T
function vSet(target: object, key: string | number, value: any): any {
  // Simply set the property directly - Vue 3's reactivity system will handle the rest
  target[key] = value
  return value
}

/**
 * Direct property deletion for Vue 3 reactivity
 *
 * In Vue 3, we can directly delete properties on reactive objects due to the Proxy-based reactivity system.
 * This function maintains the same signature as Vue 2's Vue.delete for backward compatibility.
 *
 * @param target The object or array to delete the property from
 * @param key The property name or array index
 */
function vDel<T, K extends keyof T>(target: T, key: K): void
function vDel<T>(target: T[], key: number): void
function vDel(target: object, key: string | number): void {
  if (Array.isArray(target) && typeof key === 'number') {
    // For arrays, use splice to properly remove the element
    target.splice(key, 1)
  } else {
    // For objects, use Reflect.deleteProperty
    Reflect.deleteProperty(target, key)
  }
}

export { defineAsyncComponent, stableSync, unboundedWatch, unboundedWatchEffect, vDel, vSet }
